/**
 * Nexus Journey Telemetry
 *
 * Lightweight analytics helpers for operator journey flow events.
 *
 * Primary responsibilities:
 * - Emit consistent entry and handoff events across Nexus flows
 * - Keep telemetry payload shape stable for future analysis
 *
 * Dependencies:
 * - lib/analytics/track
 */

import { track } from '@/lib/analytics/track'

export type NexusJourneyAction = 'entry' | 'cta_click' | 'handoff' | 'error'

export type NexusJourneySurface =
  | 'onboarding'
  | 'templates'
  | 'agents'
  | 'agents_new'
  | 'agent_testing'
  | 'integrations'
  | 'integrations_marketplace'
  | 'conversations'
  | 'conversations_detail'
  | 'bookings'
  | 'crm'
  | 'analytics'
  | 'workforce'
  | 'autonomous'

type NexusJourneyTelemetryEvent = {
  action: NexusJourneyAction
  surface: NexusJourneySurface
  target?: string
  source?: string
  return_to?: string | null
  first_run?: string | null
  workspace_id?: string | null
  metadata?: Record<string, string | number | boolean | null | undefined>
}

function withDefinedValues(
  value: Record<string, string | number | boolean | null | undefined>
): Record<string, string | number | boolean | null> {
  return Object.fromEntries(
    Object.entries(value).filter(([, currentValue]) => currentValue !== undefined)
  ) as Record<string, string | number | boolean | null>
}

export function trackNexusJourneyEvent(event: NexusJourneyTelemetryEvent) {
  const payload = withDefinedValues({
    action: event.action,
    surface: event.surface,
    target: event.target,
    source: event.source,
    return_to: event.return_to ?? undefined,
    first_run: event.first_run ?? undefined,
    workspace_id: event.workspace_id ?? undefined,
    ...event.metadata
  })

  void track('nexus_operator_journey_event', payload)
}
